/* eslint-disable import/no-anonymous-default-export */
import { plugin, theme } from '@shc/ui'

const svgToDataUri = require('mini-svg-data-uri')

const selectCarat = (fill: string) =>
  svgToDataUri(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
  <path fill="${fill}" d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z" />
</svg>`)

/** @type {import('tailwindcss').Config} */

export default {
  presets: [theme],
  content: ['app/**/*.{ts,tsx}','./node_modules/@shc/ui/dist/**/**/*.{ts,tsx,jsx,js}'],
  safelist: [],
  theme: {
    backgroundSize: {
      'auto': 'auto',
      'cover': 'cover',
      'contain': 'contain',
      '50%': '50%',
      '75%': '75%',
    },
    borderRadius: {
      DEFAULT: '0.3125rem', // 5px if base font-size is 16px
      none: '0px',
      full: '9999px',
      xl: '0.625rem',
    },
    borderWidth: {
      DEFAULT: '1px',
      0: '0',
      2: '2px',
      3: '3px',
      4: '4px',
    },
    boxShadow: {
      DEFAULT: '2px 2px 6px rgba(0, 0, 0, 0.1)',
    },
    colors: {
      inherit: 'inherit',
      current: 'currentColor',
      transparent: 'transparent',
      black: '#000',
      white: '#fff',
      warning: '#DD7936',
      lightGradient: 'var(--lightGradient)',
      darkGradient: 'var(--darkGradient)',
      link: '#18808C',
      linkhover: '#01697D',
      primary: {
        50: 'var(--primary50)',
        100: 'var(--primary100)',
        200: 'var(--primary200)',
        700: 'var(--primary700)',
        800: 'var(--primary800)',
        DEFAULT: 'var(--primaryDEFAULT)'
      },
      secondary: {
        50: 'var(--secondary50)',
        200: 'var(--secondary200)',
        700: 'var(--secondary700)',
        DEFAULT: 'var(--secondaryDEFAULT)'
      },
      tertiary: {
        50: '#f3faff',
        600: '#00548e',
        800: '#0d2f4f',
        DEFAULT: '#18808C',
      },
      accent: {
        50: '#fefbf0',
        100: '#fdf5d9',
        400: '#cca360',
        600: '#c9510D',
        700: '#a54209',
        DEFAULT: '#c9510D',
      },
      gray: {
        50: '#FAFCFC',
        75: '#eceff1',
        100: '#edefef',
        200: '#cfd8dc',
        500: '#8793AB',
        700: '#42484d',
      },
      success: '#45a57c',
      danger: '#ba4534',
    },
    fontSize: {
      'xs': ['0.8125rem', '1.0625rem'],
      'sm': ['0.875rem', '1.5rem'],
      'base': ['1rem', '1.75rem'],
      'lg': ['1.125rem', '1.75rem'],
      'xl': ['1.375rem', '1.875rem'],
      '2xl': ['1.5rem', '2rem'],
      '3xl': ['1.75rem', '2.25rem'],
      '4xl': ['2.125rem', '2.625rem'],
      '5xl': ['2.5rem', '4rem'],
      '6xl': ['3rem', '4rem'],
      '7xl': ['4.5rem', '6rem'],
    },
    fontFamily: {
      sans: ['var(--font-montserrat)'],
      bold: ['var(--font-montserrat)'],
      semibold: ['var(--font-montserrat)'],
      medium: ['var(--font-montserrat)'],
    },
    transitionTimingFunction: {
      'DEFAULT': 'cubic-bezier(0.4, 0, 0.2, 1)',
      'in': 'cubic-bezier(0.4, 0, 1, 1)',
      'out': 'cubic-bezier(0, 0, 0.2, 1)',
      'in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
      'bounce-in': 'cubic-bezier(0.345, 0.115, 0.135, 1.42)',
    },
    extend: {
      // Any key here does a deep merge with the default theme from tailwind
      backgroundImage: {
        'select-carat': `url("${selectCarat('#42484d')}")`,
        'select-carat-disabled': `url("${selectCarat('#00000066')}")`,
        'cacti': "url('/assets/backgrounds/cacti.webp')",
        'coastline': "url('/assets/backgrounds/coastline.webp')",
        'floral': "url('/assets/backgrounds/floral.webp')",
        'gold-starburst': "url('/assets/backgrounds/gold-burst.webp')",
        'gold-starburst-right': "url('/assets/backgrounds/gold-burst-right.webp')",
        'petals': "url('/assets/backgrounds/petals.webp')",
        'succulent': "url('/assets/backgrounds/succulent.webp')",
        'yucca': "url('/assets/backgrounds/yucca.webp')",
        'white-starburst': "url('/assets/backgrounds/white-burst.webp')",
        'white-starburst-left': "url('/assets/backgrounds/white-burst-left.webp')",
      },
      dropShadow: {
        'sm-bottom': '0 2px 2px rgba(0, 0, 0, 0.05)',
        'darken-hover': '0 4px 3px rgba(0, 0, 0, 0.15)', // Increase opacity for a darker shadow
      },
      lineHeight: {
        // Ensuring we have line height consistency for the utility classes
        3: '1.0625rem',
        6: '1.5rem',
        7: '1.75rem',
        8: '1.875rem',
        9: '2rem',
        10: '2.25rem',
        11: '2.625rem',
        12: '4rem',
      },
      spacing: {
        // Add custom spacing hacks for that pixel perfect render!
        4.25: '1.125rem', // 18px
        4.5: '1.1875rem', // 19px; eg: large button with outline
        15: '3.75rem', // 60px
        25: '6.25rem', // 100px
      },
      debugScreens: {
        position: ['bottom', 'right'],
      },
      width: {
        'grid-2': 'calc( ( 100% - ( 1.25rem * 1 ) ) / 2 )',
        'grid-3': 'calc( ( 100% - ( 1.25rem * 2 ) ) / 3 )',
        'grid-4': 'calc( ( 100% - ( 1.25rem * 3 ) ) / 4 )',
        'grid-5': 'calc( ( 100% - ( 1.25rem * 4 ) ) / 5 )',
        'grid-3/12': 'calc( ( 100% - ( 1.25rem * 1 ) ) / (12/3) )',
        'grid-5/12': 'calc( ( 100% - ( 1.25rem * 1 ) ) / (12/5) )',
        'grid-7/12': 'calc( ( 100% - ( 1.25rem * 1 ) ) / (12/7) )',
        'grid-9/12': 'calc( ( 100% - ( 1.25rem * 1 ) ) / (12/9) )',
      },
      maxHeight: {
        '10/12': '83.333333%',
      },
      zIndex: {
        1: 1,
      },
      keyframes: {
        scrollLeft: {
          '0%': { transform: 'translateX(0)' },
          '20%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(-50%)' },
        },
      },
      animation: {
        scrollLeft: 'scrollLeft 15s ease-in-out infinite',
      },
    },
  },
  plugins: [require('@tailwindcss/forms'), require('tailwindcss-debug-screens'), plugin],
}
